import type { OptionImageSetFormRef } from "./forms/ProductsOptionImageSetForm";

import * as React from "react";

import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useModal } from "@editor/hooks/useModal";
import { useAllowScroll } from "@editor/utils/hooks/useAllowScroll";
import { routes } from "@editor/utils/router";
import { trpc } from "@editor/utils/trpc";

import { zodResolver } from "@hookform/resolvers/zod";
import Button from "@replo/design-system/components/button/Button";
import EmptyState from "@replo/design-system/components/empty-state/EmptyState";
import { Modal } from "@replo/design-system/components/modal/Modal";
import PageHeader from "@replo/design-system/components/PageHeader";
import {
  Card,
  CardContent,
  CardTitle,
} from "@replo/design-system/components/shadcn/core/card";
import { Textarea } from "@replo/design-system/components/textarea/Textarea";
import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import startCase from "lodash-es/startCase";
import { Edit, ExternalLink, Loader2, Package, Save, X } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { z } from "zod";

import { ProductsOptionImageSetForm } from "./forms/ProductsOptionImageSetForm";
import { ProductsOptionsEditor } from "./ProductsOptionsEditor";
import {
  getPriceRange,
  getStatusBadgeClasses,
  openShopifyProductPageUrl,
} from "./utils";

const productFormSchema = z.object({
  title: z.string().min(1, "Product name is required"),
  description: z.string().nullable(),
  slug: z.string().nullable(),
  status: z.enum(["active", "draft", "archived"]),
});

type ProductFormValues = z.infer<typeof productFormSchema>;

type ModalState =
  | {
      type: "imageSet";
      optionName: string;
      optionValue: string;
      optionValueId: string;
      currentAssetIds: string[];
    }
  | {
      type: "thumbnail";
      optionName: string;
      optionValue: string;
      optionValueId: string;
      currentColor: string | null;
      currentImage: string | null;
    };

interface LoadingScreenProps {
  title: string;
  description: string;
}

const LoadingScreen = ({ title, description }: LoadingScreenProps) => (
  <div className="flex h-screen items-center justify-center p-6">
    <EmptyState
      icon={<Loader2 className="animate-spin" />}
      title={title}
      description={description}
      size="sm"
    />
  </div>
);

const NEW_PRODUCT_DEFAULTS: ProductFormValues = {
  title: "New Product",
  description: "",
  slug: null,
  status: "draft",
};

export function ProductsEditor() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const currentProjectId = useCurrentProjectId();
  const utils = trpc.useUtils();
  const { openModal } = useModal();

  const { project } = useCurrentProjectContext();
  const shopifyStore = project?.integrations?.shopify?.store;

  const onEditInShopify = React.useCallback(() => {
    if (shopifyStore?.shopifyUrl) {
      openShopifyProductPageUrl(shopifyStore.shopifyUrl);
    }
  }, [shopifyStore?.shopifyUrl]);

  const [isEditing, setIsEditing] = React.useState(false);
  const [modalState, setModalState] = React.useState<ModalState | null>(null);

  const isNewProduct = productId === "new";

  const handleBack = React.useCallback(() => navigate(-1), [navigate]);

  const showProductError = React.useCallback(() => {
    openModal({
      type: "fullPageErrorModal",
      props: {
        details: {
          header: "Unable to access product",
          subheader: "There was an error loading this product",
          message: "This product could not be found or accessed.",
          messageDetail:
            "If this problem persists, please reach out to support@replo.app",
          isRecoverable: true,
          callToAction: {
            type: "callback",
            name: "Go Back",
            onClick: handleBack,
          },
        },
      },
    });
  }, [openModal, handleBack]);

  const {
    data: product,
    isLoading: isLoadingProduct,
    error: productError,
  } = trpc.product.get.useQuery(
    { type: "id", id: productId! },
    {
      enabled: Boolean(productId) && !isNewProduct,
    },
  );

  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productFormSchema),
    defaultValues: NEW_PRODUCT_DEFAULTS,
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
  } = form;

  useAllowScroll();
  const optionImageSetFormRef = React.useRef<OptionImageSetFormRef>(null);
  const hasInitiallyLoaded = React.useRef(false);

  const createProductMutation =
    trpc.product.createOrUpdateReploProduct.useMutation({
      onSuccess: (newProduct) => {
        void utils.product.list.invalidate({
          projectId: currentProjectId ?? "",
        });
        if (currentProjectId && routes?.shops?.products?.edit) {
          navigate(
            generatePath(routes.shops.products.edit, {
              projectId: currentProjectId,
              productId: newProduct.id,
            }),
            { replace: true },
          );
        }
      },
    });

  const handleCreateNewProduct = React.useCallback(() => {
    if (currentProjectId) {
      createProductMutation.mutate({
        projectId: currentProjectId,
        productData: {
          ...NEW_PRODUCT_DEFAULTS,
          id: uuidv4(),
        },
      });
    }
  }, [currentProjectId, createProductMutation]);

  React.useEffect(() => {
    if (
      isNewProduct &&
      !createProductMutation.isPending &&
      !createProductMutation.error &&
      !createProductMutation.isSuccess
    ) {
      handleCreateNewProduct();
    }
  }, [
    isNewProduct,
    createProductMutation.isPending,
    createProductMutation.error,
    createProductMutation.isSuccess,
    handleCreateNewProduct,
  ]);

  React.useEffect(() => {
    if (productError) {
      showProductError();
    }
  }, [productError, showProductError]);

  React.useEffect(() => {
    if (createProductMutation.error) {
      showProductError();
    }
  }, [createProductMutation.error, showProductError]);

  React.useEffect(() => {
    if (product && !hasInitiallyLoaded.current) {
      reset({
        title: product.title ?? NEW_PRODUCT_DEFAULTS.title,
        description: product.description ?? NEW_PRODUCT_DEFAULTS.description,
        slug: product.slug,
        status: product.status ?? NEW_PRODUCT_DEFAULTS.status,
      });
      hasInitiallyLoaded.current = true;
    }
  }, [product, reset]);

  const updateProductMutation =
    trpc.product.createOrUpdateReploProduct.useMutation({
      onSuccess: (updatedProduct) => {
        if (updatedProduct && productId && !isNewProduct) {
          void utils.product.get.invalidate({ type: "id", id: productId });
          setIsEditing(false);
        }
      },
    });

  const handleCancel = React.useCallback(() => {
    if (product) {
      setIsEditing(false);
    }
  }, [product]);

  const handleEditClick = React.useCallback(() => {
    if (product) {
      reset({
        title: product.title ?? NEW_PRODUCT_DEFAULTS.title,
        description: product.description ?? NEW_PRODUCT_DEFAULTS.description,
        slug: product.slug,
        status: product.status ?? NEW_PRODUCT_DEFAULTS.status,
      });
      setIsEditing(true);
    }
  }, [product, reset]);

  const handleModalSubmitSuccess = () => setModalState(null);

  const handleModalCancel = () => setModalState(null);

  const onSubmit = (data: ProductFormValues) => {
    if (!currentProjectId || !product || product.referenceType !== "REPLO") {
      return;
    }

    const payload = {
      id: product.id,
      title: data.title,
      description: data.description,
      slug: data.slug,
      status: data.status,
    };

    updateProductMutation.mutate({
      projectId: currentProjectId,
      productData: payload,
    });
  };

  const getModalTitle = (modalState: ModalState | null): string => {
    if (!modalState) {
      return "";
    }

    if (modalState.type === "imageSet") {
      return `Manage Images: ${modalState.optionName} - ${modalState.optionValue}`;
    } else {
      return `Manage Thumbnail: ${modalState.optionName} - ${modalState.optionValue}`;
    }
  };

  if (isNewProduct && createProductMutation.isPending) {
    return (
      <LoadingScreen
        title="Creating new product..."
        description="Setting up your new product. This will only take a moment."
      />
    );
  }

  if (isLoadingProduct) {
    return (
      <LoadingScreen
        title="Loading product details..."
        description="Fetching product information from the server."
      />
    );
  }

  if (!product) {
    return (
      <LoadingScreen
        title="Loading product..."
        description="Please wait while we load the product information."
      />
    );
  }

  const modal = (
    <Modal
      isOpen={Boolean(modalState)}
      onOpenChange={(open) => {
        if (!open) {
          if (modalState && optionImageSetFormRef.current) {
            optionImageSetFormRef.current.submit();
          } else {
            handleModalCancel();
          }
        }
      }}
      title={getModalTitle(modalState)}
      description={
        modalState?.type === "imageSet"
          ? "Select images from your library or upload new ones for this option value."
          : "Assign an image or color as the thumbnail for this option value."
      }
      size="lg"
    >
      {modalState && (
        <ProductsOptionImageSetForm
          ref={optionImageSetFormRef}
          optionValueId={modalState.optionValueId}
          optionName={modalState.optionName}
          optionValue={modalState.optionValue}
          currentAssetIds={
            modalState.type === "imageSet" ? modalState.currentAssetIds : []
          }
          currentColor={
            modalState.type === "thumbnail" ? modalState.currentColor : null
          }
          currentImage={
            modalState.type === "thumbnail" ? modalState.currentImage : null
          }
          mode={modalState.type === "imageSet" ? "imageList" : "thumbnail"}
          onSubmitSuccess={handleModalSubmitSuccess}
          onCancel={handleModalCancel}
          projectId={currentProjectId!}
          productId={product.id}
        />
      )}
    </Modal>
  );

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        titleComponent={
          <div className="flex items-center gap-3">
            <h1 className="header-h1 text-default">{product.title}</h1>
            <div
              className={twMerge(
                "inline-block rounded-full px-2 py-0.5 text-xs font-medium",
                getStatusBadgeClasses(product.status),
              )}
            >
              {startCase(product.status)}
            </div>
          </div>
        }
        onBackClick={handleBack}
        actions={
          <>
            <Button
              variant="secondary"
              size="base"
              endEnhancer={<ExternalLink size={14} />}
              onClick={onEditInShopify}
            >
              Edit in Shopify
            </Button>
            {isEditing && (
              <Button
                variant="secondary"
                onClick={handleCancel}
                size="sm"
                disabled={updateProductMutation.isPending}
              >
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
            )}
            {product.referenceType === "REPLO" && isEditing && (
              <Button
                onClick={(e) => {
                  void handleSubmit(onSubmit)(e);
                }}
                disabled={!isDirty || updateProductMutation.isPending}
                variant="primary"
                size="sm"
              >
                <Save className="mr-2 h-4 w-4" />
                {updateProductMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            )}
            {product.referenceType === "REPLO" && !isEditing && (
              <Button onClick={handleEditClick} variant="primary" size="sm">
                <Edit className="mr-2 h-4 w-4" />
                Edit Product
              </Button>
            )}
          </>
        }
      />

      <main className="flex-1 overflow-y-auto p-6">
        <form
          onSubmit={(e) => {
            void handleSubmit(onSubmit)(e);
          }}
          className="space-y-6"
        >
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              <div className="flex">
                {/* Left side - Image taking full height */}
                <div className="w-48 flex-shrink-0">
                  {product.featuredMedia?.url ? (
                    <img
                      src={product.featuredMedia.url}
                      alt={product.title}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="bg-component text-muted flex h-full w-full items-center justify-center">
                      <Package className="h-12 w-12" />
                    </div>
                  )}
                </div>

                {/* Right side - Compact info */}
                <div className="flex-1 p-6">
                  <div className="space-y-3">
                    {/* Title and Status */}
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-xl">{product.title}</CardTitle>
                      <div
                        className={twMerge(
                          "inline-block rounded-full px-2 py-0.5 text-xs font-medium",
                          getStatusBadgeClasses(product.status),
                        )}
                      >
                        {startCase(product.status)}
                      </div>
                    </div>

                    {/* Price */}
                    <span className="text-foreground-default text-lg font-semibold">
                      {getPriceRange(product.variants)}
                    </span>

                    {/* Options */}
                    <div>
                      <label className="text-foreground-muted block text-sm font-medium mb-1">
                        Options
                      </label>
                      {product.options && product.options.length > 0 ? (
                        <div className="space-y-1">
                          {product.options.map((option) => {
                            const optionCount =
                              option.optionValues?.length ?? 0;
                            return (
                              <Tooltip
                                key={option.id}
                                content={`${option.title}: ${option.optionValues?.map((ov) => ov.value).join(", ") ?? "No values"}`}
                                triggerAsChild
                                side="top"
                              >
                                <div className="text-sm cursor-pointer hover:text-foreground transition-colors">
                                  <span className="font-medium text-foreground-default">
                                    {option.title}:{" "}
                                  </span>
                                  <span className="text-muted">
                                    {optionCount}{" "}
                                    {optionCount > 1 ? "options" : "option"}
                                  </span>
                                </div>
                              </Tooltip>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-sm text-muted">No options</div>
                      )}
                    </div>

                    <div>
                      <label className="text-foreground-muted block text-sm font-medium mb-1">
                        Description
                      </label>
                      {isEditing ? (
                        <Controller
                          name="description"
                          control={control}
                          render={({ field }) => (
                            <Textarea
                              id="productDescriptionEditor"
                              value={field.value ?? ""}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              placeholder="Product description..."
                              layoutClassName="min-h-[60px] max-h-[60px]"
                            />
                          )}
                        />
                      ) : (
                        <div className="text-sm text-foreground-default line-clamp-2">
                          {product.description ? (
                            <div
                              className="line-clamp-2"
                              dangerouslySetInnerHTML={{
                                __html: product.description,
                              }}
                            />
                          ) : (
                            <div className="text-muted italic">
                              No description
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {product.options && product.options.length > 0 && (
            <ProductsOptionsEditor
              product={product}
              onThumbnailClick={(params) => {
                setModalState({
                  type: "thumbnail",
                  ...params,
                });
              }}
              onImageSetClick={(params) => {
                setModalState({
                  type: "imageSet",
                  ...params,
                });
              }}
            />
          )}

          {modal}
        </form>
      </main>
    </div>
  );
}
